/**
 * Pure rules for the sub-track move: which destinations the server would
 * accept, whether a drag's chosen destination is one of them, and how a server
 * rejection is turned into copy. Kept out of the modal for the same reason
 * track-drag.ts is kept out of Tracks.tsx — both entry points (the cross-parent
 * drag and the context-menu picker) decide with these without rendering.
 */
import { TrackMutationError } from '../track-mutation-error';
import type { TrackPreview } from './types';

/**
 * Why a parent track cannot receive the sub-track. Only a name collision is
 * ever surfaced to the coach: the remaining exclusions (another sub-track, an
 * archived track, the current parent) are structural, so they are dropped from
 * the picker instead of being shown as unavailable.
 */
export type MoveDestinationBlockReason = 'nameCollision';

export interface BlockedMoveDestination {
  track: TrackPreview;
  reason: MoveDestinationBlockReason;
}

export interface MoveDestinationOptions {
  /** Parents the server would accept — the picker offers these. */
  eligible: TrackPreview[];
  /** Parents the server would refuse, paired with the reason to show. */
  blocked: BlockedMoveDestination[];
}

/**
 * Splits the team's parent tracks into destinations the move can complete onto
 * and ones it cannot, so the picker can offer the first group and explain the
 * second instead of silently dropping it.
 *
 * A parent is ineligible when the sub-track already lives there, when it is
 * archived, or when it already holds a sub-track with the same name
 * (case-insensitive) — sibling names must stay unique within a parent, and the
 * server rejects a collision outright.
 *
 * Archived siblings count as a collision because the server counts them:
 * unarchiving a parent cascades to its sub-tracks, so an archived same-name
 * sibling would come back as a second sub-track of that name under one parent.
 * Counting them is what keeps the eligible list equal to the moves that can
 * complete — a destination the server would refuse is not offered.
 */
export function classifyMoveDestinations(
  subTrack: TrackPreview,
  currentParentTrackId: string,
  allTracks: TrackPreview[],
): MoveDestinationOptions {
  const subTrackName = subTrack.name.toLowerCase();
  const options: MoveDestinationOptions = { eligible: [], blocked: [] };

  for (const candidate of allTracks) {
    // Only a parent track can receive a sub-track — a sub-track has no children.
    if (candidate.parentTrackId != null) continue;
    // An archived track is not a destination, and must not come back as one.
    if (candidate.archivedAt != null) continue;
    // The sub-track already lives here.
    if (candidate.id === currentParentTrackId) continue;

    const hasNameCollision = allTracks.some(
      (sibling) =>
        sibling.parentTrackId === candidate.id &&
        sibling.name.toLowerCase() === subTrackName,
    );

    if (hasNameCollision) {
      options.blocked.push({ track: candidate, reason: 'nameCollision' });
    } else {
      options.eligible.push(candidate);
    }
  }

  return options;
}

/** The `eligible` half of {@link classifyMoveDestinations}. */
export function eligibleMoveDestinations(
  subTrack: TrackPreview,
  currentParentTrackId: string,
  allTracks: TrackPreview[],
): TrackPreview[] {
  return classifyMoveDestinations(subTrack, currentParentTrackId, allTracks)
    .eligible;
}

/**
 * Whether a cross-parent drag's destination is one the move can actually
 * complete onto — the same rule `eligibleMoveDestinations` offers in the
 * picker, applied to a destination the drag already chose rather than to the
 * full list of parents.
 */
export function isEligibleMoveDestination(
  subTrack: TrackPreview,
  currentParentTrackId: string,
  destinationTrack: TrackPreview,
  allTracks: TrackPreview[],
): boolean {
  return eligibleMoveDestinations(
    subTrack,
    currentParentTrackId,
    allTracks,
  ).some((track) => track.id === destinationTrack.id);
}

/**
 * Server rejections are English-only, so the one the coach is most likely to
 * hit is mapped to translated copy here rather than shown raw. The picker
 * already withholds every collision it can compute, so this is left for the
 * collisions it cannot see — a same-name sibling created or renamed at the
 * destination after the list was built. Anything else falls back to the generic
 * error, because there is nothing more specific to say.
 */
export function moveSubTrackErrorMessage(
  error: unknown,
  messages: { nameCollision: string; fallback: string },
): string {
  return error instanceof TrackMutationError &&
    error.rejectionReason === 'DESTINATION_NAME_TAKEN'
    ? messages.nameCollision
    : messages.fallback;
}
